// SCR-STAFF-415 — تنظیمات مالی (بخش AP-BIL-01؛ US-BIL-013، 021): واحد گردکردن مبلغ قابل پرداخت (پیش‌فرض ۱۰٬۰۰۰ ریال به پایین — BR-BIL-014)، بستن خودکار
// مراجعه در پایان روز شعبه (AC-BIL-004.3) و نمای فقط‌خواندنی سری‌های شماره‌گذاری به ازای شعبه و سال مالی (BR-BIL-023؛ C-AGENT-547). AP-BIL-02: روش‌های
// پرداخت مستأجر، شمارش کور، ترتیب مصرف کیف پول، مهلت لینک پرداخت، آستانه تنخواه، سقف بدهی بدون تأیید و تعریف صندوق‌ها (CashRegistersSection).
// AP-BIL-04: طرح‌های اعتبار هدیه کیف پول (WalletPromotionsSection؛ US-BIL-092).
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BilNumberingSeriesView, type BilPaymentMethod } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, ListPage, useMoneyFormatter, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { parseRials } from './CashierPage';
import { CashRegistersSection } from './CashRegistersSection';
import { WalletPromotionsSection } from './WalletPromotionsSection';
import { bilKeys, num, rial, SCR_STAFF_415 } from './common';

export function BillingSettingsPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const format = useMoneyFormatter();
  const settings = useQuery({ queryKey: bilKeys.settings(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/settings')) });
  const numbering = useQuery({ queryKey: bilKeys.numbering(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/settings/numbering')) });
  const [rounding, setRounding] = useState('10000');
  const [autoClose, setAutoClose] = useState(true);
  const [methods, setMethods] = useState<BilPaymentMethod[]>([]);
  const [blindCount, setBlindCount] = useState(true);
  const [giftFirst, setGiftFirst] = useState(true);
  const [linkHours, setLinkHours] = useState('24');
  const [pettyThreshold, setPettyThreshold] = useState('');
  const [debtLimit, setDebtLimit] = useState('');
  const [eInvoice, setEInvoice] = useState(false);
  const [economicCode, setEconomicCode] = useState('');
  const access = useAccess();
  useEffect(() => {
    if (settings.data) {
      setRounding(String(num(settings.data.roundingUnit)));
      setAutoClose(settings.data.autoCloseEnabled);
      setMethods([...(settings.data.enabledMethods ?? [])]);
      setBlindCount(settings.data.blindCount);
      setGiftFirst(settings.data.walletGiftFirst);
      setLinkHours(String(num(settings.data.paymentLinkHours)));
      setPettyThreshold(String(settings.data.pettyCashApprovalThreshold?.amount ?? ''));
      setDebtLimit(String(settings.data.patientDebtLimit?.amount ?? 0));
      setEInvoice(settings.data.eInvoiceEnabled);
      setEconomicCode(settings.data.economicCode ?? '');
    }
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/billing/settings', {
          body: {
            roundingUnit: Number(rounding),
            autoCloseEnabled: autoClose,
            enabledMethods: methods,
            blindCount,
            walletGiftFirst: giftFirst,
            paymentLinkHours: Number(linkHours) || 24,
            pettyCashApprovalThreshold: rial(parseRials(pettyThreshold)),
            patientDebtLimit: rial(parseRials(debtLimit)),
            pettyCashCategories: settings.data?.pettyCashCategories ?? null,
            // AP-BIL-05: فعال‌سازی بی‌کد اقتصادی رد می‌شود (PHX-BIL-1804).
            eInvoiceEnabled: eInvoice,
            economicCode: economicCode || null,
          },
        }),
      ),
    onSuccess: (view) => queryClient.setQueryData(bilKeys.settings(session?.tenantId), view),
  });
  const columns: DataTableColumn<BilNumberingSeriesView>[] = [
    { id: 'branch', headerKey: 'bil:settings.columns.branch', cell: (row) => row.branchName ?? row.branchCode },
    { id: 'code', headerKey: 'bil:settings.columns.code', cell: (row) => <bdi dir="ltr">{row.branchCode}</bdi> },
    { id: 'year', headerKey: 'bil:settings.columns.year', cell: (row) => faDigits(num(row.fiscalYear)) },
    { id: 'type', headerKey: 'bil:settings.columns.type', cell: (row) => t(`bil:settings.document_type.${row.documentType}`, { defaultValue: row.documentType }) },
    { id: 'last', headerKey: 'bil:settings.columns.last', cell: (row) => <bdi dir="ltr">{row.lastNumber ?? '—'}</bdi> },
    { id: 'next', headerKey: 'bil:settings.columns.next', cell: (row) => <bdi dir="ltr" data-testid="bil-numbering-next">{row.nextNumber}</bdi> },
  ];
  return (
    <ListPage screenId={SCR_STAFF_415} titleKey="bil:settings.title" subtitleKey="bil:settings.subtitle">
      {settings.error ? <ErrorState error={settings.error} onRetry={() => void settings.refetch()} /> : null}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1">{t('bil:settings.rounding_title')}</Typography>
          <TextField
            select
            label={t('bil:settings.rounding')}
            value={rounding}
            onChange={(event) => setRounding(event.target.value)}
            helperText={t('bil:settings.rounding_help')}
            sx={{ maxWidth: 360 }}
            slotProps={{ htmlInput: { 'data-testid': 'bil-settings-rounding' } }}
          >
            {(settings.data?.allowedRoundingUnits ?? [0, 1000, 5000, 10000, 50000, 100000]).map((unit) => (
              <MenuItem key={String(unit)} value={String(num(unit))}>
                {num(unit) === 0 ? t('bil:settings.no_rounding') : format(num(unit), 'IRR')}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={autoClose} onChange={(event) => setAutoClose(event.target.checked)} slotProps={{ input: { 'aria-label': t('bil:settings.auto_close') } }} />}
            label={t('bil:settings.auto_close')}
          />
          <Typography variant="body2" color="text.secondary">
            {t('bil:settings.reopen_window', { days: faDigits(num(settings.data?.reopenWindowDays ?? 7)) })}
          </Typography>
          <Typography variant="subtitle1">{t('bil:settings.cashier_title')}</Typography>
          <FormGroup row data-testid="bil-settings-methods">
            {(settings.data?.availableMethods ?? []).map((method) => (
              <FormControlLabel
                key={method}
                control={
                  <Checkbox
                    checked={methods.includes(method)}
                    onChange={(event) => setMethods((current) => (event.target.checked ? [...current, method] : current.filter((item) => item !== method)))}
                  />
                }
                label={t(`bil:method.${method}`)}
              />
            ))}
          </FormGroup>
          <FormControlLabel
            control={<Switch checked={blindCount} onChange={(event) => setBlindCount(event.target.checked)} slotProps={{ input: { 'aria-label': t('bil:settings.blind_count') } }} />}
            label={t('bil:settings.blind_count')}
          />
          <FormControlLabel
            control={<Switch checked={giftFirst} onChange={(event) => setGiftFirst(event.target.checked)} slotProps={{ input: { 'aria-label': t('bil:settings.gift_first') } }} />}
            label={t('bil:settings.gift_first')}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('bil:settings.link_hours')} value={linkHours} onChange={(event) => setLinkHours(event.target.value.replace(/\D/g, ''))} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-settings-link-hours' } }} />
            <TextField label={t('bil:settings.petty_threshold')} value={pettyThreshold} onChange={(event) => setPettyThreshold(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }} />
            <TextField label={t('bil:settings.debt_limit')} value={debtLimit} onChange={(event) => setDebtLimit(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-settings-debt-limit' } }} />
          </Stack>
          {/* AP-BIL-05 (AC-BIL-023.1): ارسال صورتحساب الکترونیک مودیان و کد اقتصادی مرکز. */}
          <Typography variant="subtitle1">{t('bil:settings.e_invoice_section')}</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={eInvoice}
                onChange={(event) => setEInvoice(event.target.checked)}
                slotProps={{ input: { 'aria-label': t('bil:settings.e_invoice_enabled') } }}
                data-testid="bil-settings-einvoice"
              />
            }
            label={t('bil:settings.e_invoice_enabled')}
          />
          <TextField
            label={t('bil:settings.economic_code')}
            helperText={t('bil:settings.economic_code_hint')}
            value={economicCode}
            onChange={(event) => setEconomicCode(event.target.value.replace(/\D/gu, ''))}
            sx={{ maxWidth: 320 }}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', maxLength: 20, 'data-testid': 'bil-settings-economic-code' } }}
          />
          {save.error ? <ErrorState error={save.error} compact /> : null}
          {save.isSuccess ? <Alert severity="success">{t('bil:settings.saved')}</Alert> : null}
          <Button variant="contained" sx={{ alignSelf: 'flex-start' }} onClick={() => save.mutate()} disabled={save.isPending || !settings.data} data-testid="bil-settings-save">
            {t('bil:settings.save')}
          </Button>
        </Stack>
      </Paper>
      {access.can('bil.cash_register.manage') ? <CashRegistersSection /> : null}
      {access.accessMode === 'Full' && access.can('bil.wallet.promotion.manage') ? <WalletPromotionsSection /> : null}
      <Typography variant="subtitle1">{t('bil:settings.numbering_title')}</Typography>
      {numbering.data ? (
        <Typography variant="body2" color="text.secondary">
          {t('bil:settings.pattern')}: <bdi dir="ltr">{numbering.data.pattern}</bdi>
        </Typography>
      ) : null}
      <DataTable
        captionKey="bil:settings.numbering_title"
        columns={columns}
        rows={numbering.data?.items}
        getRowId={(row) => `${row.branchId}-${row.fiscalYear}-${row.documentType}`}
        isLoading={numbering.isPending}
        error={numbering.error}
        onRetry={() => void numbering.refetch()}
        emptyTitleKey="bil:settings.numbering_empty"
      />
    </ListPage>
  );
}
